import React from "react";
import moment from "moment";
import { FiCalendar } from "react-icons/fi";
import { BookingDatesProps } from "../../types/messages.types";

/**
 * BookingDates Component
 * Displays check-in and check-out dates with improved design
 */
const BookingDates: React.FC<BookingDatesProps> = ({
  checkInDate,
  checkOutDate,
}) => {
  // Don't render if dates are missing
  if (!checkInDate || !checkOutDate) {
    return null;
  }

  // Convert UTC to local timezone and format date
  const formatDate = (dateString: string) => {
    // Try parsing with moment - assume UTC from backend
    //const parsedDate = moment.utc(dateString).local();
    const parsedDate = moment.utc(dateString);

    // If valid, format nicely
    if (parsedDate.isValid()) {
      return {
        day: parsedDate.format("DD"),
        month: parsedDate.format("MMM"),
        year: parsedDate.format("YYYY"),
        full: parsedDate.format("MMM DD, YYYY"),
      };
    }

    // If already formatted (like "Nov 17, 2025"), use as-is
    return {
      full: dateString,
      formatted: dateString,
    };
  };

  const checkIn = formatDate(checkInDate);
  const checkOut = formatDate(checkOutDate);

  return (
    <div className="bg-gradient-to-br from-[#F56948]/5 to-[#E5593D]/5 dark:from-[#F56948]/10 dark:to-[#E5593D]/10 border-2 border-[#F56948]/20 dark:border-[#F56948]/30 rounded-2xl p-5 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#F56948] flex items-center justify-center">
          <FiCalendar className="text-white" size={20} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">
            Booking Dates
          </h4>
        </div>
      </div>

      {/* Dates Container */}
      <div className="relative">
        <div className="grid grid-cols-2 gap-4">
          {/* Check-in */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2 uppercase tracking-wide">
              Check-in
            </p>
            {checkIn.day ? (
              <>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {checkIn.day}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 font-medium">
                  {checkIn.month} {checkIn.year}
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                {checkIn.full}
              </p>
            )}
          </div>

          {/* Check-out */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2 uppercase tracking-wide">
              Check-out
            </p>
            {checkOut.day ? (
              <>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {checkOut.day}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 font-medium">
                  {checkOut.month} {checkOut.year}
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                {checkOut.full}
              </p>
            )}
          </div>
        </div>

        {/* Arrow between dates */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden md:block">
          <div className="w-10 h-10 rounded-full bg-[#F56948] flex items-center justify-center shadow-lg">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDates;